import { CaretDownOutlined, CaretUpOutlined, DatabaseOutlined } from "@ant-design/icons";
import { EVisionDatabaseTypes, IDatabaseServiceConfig } from "@kuika/kuika-cl-model";
import { memo, useEffect, useMemo, useState } from "react";
import { Handle, Position, useReactFlow, useStore } from "reactflow";
import KuikaDropdownMenu from "../../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSvg from "../../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { useHistory } from "../history-context";
import ExtendingConnector from "./ExtendingConnector";
import { NODE_TOOLTIPS } from "./nodesInfoContent";
import { useNodeCreation } from "./useNodeCreation";
import baseStyles from "./BaseNode.module.css";

interface DatabaseServiceNodeData {
  type: "DatabaseService";
  title: string;
  config?: IDatabaseServiceConfig;
  status?: "idle" | "connecting" | "active";
}

interface DatabaseServiceNodeProps {
  data: DatabaseServiceNodeData;
  id: string;
  selected?: boolean;
}

const NODE_CATEGORY_STYLES = {
  service: {
    primaryColor: "#7c3aed",
    headerBackground: "rgba(124, 58, 237, 0.1)",
    iconColor: "#7c3aed",
    handleColor: "#7c3aed"
  }
};

const DatabaseServiceNode = ({ data, id, selected }: DatabaseServiceNodeProps) => {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState(data.status || "idle");
  const [config, setConfig] = useState<IDatabaseServiceConfig>(() => {
    // Default config values matching backend EVisionDatabaseTypes
    const defaults: IDatabaseServiceConfig = {
      databaseType: EVisionDatabaseTypes.Mssql,
      tables: ["events"],
      // tables: ["events", "metrics", "reports"],
      retentionPolicy: {
        events: 30
        // metrics: 90,
        // reports: 365
      },
      connectionString: ""
    };
    // Merge with provided config from template
    if (data.config) {
      return {
        ...defaults,
        ...data.config,
        // Deep merge retention policy
        retentionPolicy: {
          ...defaults.retentionPolicy,
          ...data.config.retentionPolicy
        }
      };
    }
    return defaults;
  });
  const { setNodes, getNodes, getEdges } = useReactFlow();
  const { createConnectedNode } = useNodeCreation(id);
  const takeSnapshot = useHistory();

  // Check if source handle is connected - using useStore for reactivity
  const isSourceConnected = useStore(useMemo(() => (state) => state.edges.some((edge) => edge.source === id), [id]));

  const categoryStyles = NODE_CATEGORY_STYLES.service;

  const handleNodeSelect = (selectedNodeType: string) => {
    createConnectedNode(selectedNodeType);
  };

  const shouldShowConnector = !isSourceConnected || selected;
  const isCompactMode = isSourceConnected && selected;

  const renderInfoIcon = () => (
    <div className={baseStyles.nodeInfoTopRight}>
      <KuikaTooltip
        position="top"
        content={<div className={baseStyles.nodeTooltipContent}>{NODE_TOOLTIPS.databaseService.text}</div>}
      >
        <span className={`${baseStyles.nodeInfoIcon} ${baseStyles.nodrag}`}>
          <KuikaSvg name="info-circle" width={16} height={16} />
        </span>
      </KuikaTooltip>
    </div>
  );

  const updateConfig = (updates: Partial<IDatabaseServiceConfig>) => {
    // Take snapshot before each config change for undo support
    if (takeSnapshot) {
      takeSnapshot(getNodes(), getEdges());
    }

    const newConfig = { ...config, ...updates };
    setConfig(newConfig);

    setNodes((nodes) =>
      nodes.map((node) => (node.id === id ? { ...node, data: { ...node.data, config: newConfig } } : node))
    );
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Take snapshot before deleting
    if (takeSnapshot) {
      takeSnapshot(getNodes(), getEdges());
    }
    setNodes((nodes) => nodes.filter((node) => node.id !== id));
  };

  // Sync config state when node data changes from outside (e.g., undo/redo)
  useEffect(() => {
    if (data.config) {
      setConfig((prevConfig) => {
        const stringifiedPrev = JSON.stringify(prevConfig);
        const stringifiedNew = JSON.stringify(data.config);
        // Only update if actually different to avoid unnecessary re-renders
        return stringifiedPrev !== stringifiedNew ? { ...prevConfig, ...data.config } : prevConfig;
      });
    }
  }, [data.config]);

  const renderSummary = () => {
    return (
      <div className={`${baseStyles.nodeContent} ${baseStyles.nodeContentWithInfo}`}>
        {renderInfoIcon()}
        <span className={baseStyles.collapsedSummary}>
          {config.databaseType?.toUpperCase()} • {status}
        </span>
      </div>
    );
  };

  return (
    <div className={`${baseStyles.nodeContainer} ${selected ? "selected" : ""}`}>
      {selected && (
        <button
          className={`${baseStyles.deleteButton} ${baseStyles.nodrag}`}
          onClick={handleDelete}
          onMouseDown={(e) => e.stopPropagation()}
          title="Delete node"
        >
          <KuikaSvg type="common" name="x" width={14} height={14} color="var(--red-5)" />
        </button>
      )}
      <div
        className={`${baseStyles.nodeHeader} ${expanded ? baseStyles.nodeHeaderExpanded : ""}`}
        style={{ background: categoryStyles.headerBackground }}
      >
        <span className={baseStyles.nodeIcon} style={{ color: categoryStyles.iconColor }}>
          <DatabaseOutlined />
        </span>
        <span className={baseStyles.nodeTitle}>
          {data.title}
          <KuikaTooltip
            position="top"
            content={<div className={baseStyles.nodeTooltipContent}>{NODE_TOOLTIPS.databaseService.text}</div>}
          >
            <span className={`${baseStyles.nodeInfoIcon} ${baseStyles.nodrag}`}>
              <KuikaSvg name="info-circle" width={16} height={16} />
            </span>
          </KuikaTooltip>
        </span>
        <button
          onClick={() => setExpanded(!expanded)}
          onMouseDown={(e) => e.stopPropagation()}
          className={`${baseStyles.expandButton} ${baseStyles.nodrag}`}
        >
          {expanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
        </button>
      </div>

      {!expanded && renderSummary()}

      {expanded && (
        <div className={`${baseStyles.nodeContent} ${baseStyles.nodeContentWithInfo}`}>
          {renderInfoIcon()}
          <div className={baseStyles.nodeForm}>
            <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>Database Type</label>
              <div className={baseStyles.nodrag}>
                <KuikaDropdownMenu
                  options={[
                    { key: EVisionDatabaseTypes.Mssql, label: "mssql" }
                    // { key: EVisionDatabaseTypes.MySql, label: "MySQL" },
                    // { key: EVisionDatabaseTypes.PostgreSql, label: "PostgreSQL" },
                    // { key: EVisionDatabaseTypes.MongoDb, label: "MongoDB" },
                    // { key: EVisionDatabaseTypes.Redis, label: "Redis" }
                  ]}
                  selected={config.databaseType}
                  setSelected={(key) => updateConfig({ databaseType: key as EVisionDatabaseTypes })}
                  size="md"
                >
                  <div className={baseStyles.formSelect} style={{ cursor: "pointer" }}>
                    {config.databaseType === EVisionDatabaseTypes.Mssql && "mssql"}
                    {!config.databaseType && "mssql"}
                  </div>
                </KuikaDropdownMenu>
              </div>
            </div>

            <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>Connection String</label>
              <input
                type="text"
                className={`${baseStyles.formInput} ${baseStyles.nodrag}`}
                value={config.connectionString || ""}
                onChange={(e) => updateConfig({ connectionString: e.target.value })}
                placeholder="Enter connection string..."
              />
            </div>

            <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>Tables</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                {config.tables?.map((table) => (
                  <span
                    key={table}
                    style={{
                      padding: "2px 8px",
                      borderRadius: "var(--radius-xs)",
                      background: "var(--dark-2)",
                      border: "1px solid var(--dark-3)",
                      fontSize: "11px"
                    }}
                  >
                    {table}
                  </span>
                ))}
              </div>
            </div>

            {/* <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>Retention Policy (days)</label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
                <div>
                  <label className={baseStyles.formLabel} style={{ fontSize: "10px" }}>
                    Events
                  </label>
                  <input
                    type="number"
                    className={`${baseStyles.formNumber} ${baseStyles.nodrag}`}
                    value={config.retentionPolicy?.events}
                    onChange={(e) =>
                      updateConfig({
                        retentionPolicy: {
                          ...config.retentionPolicy!,
                          events: parseInt(e.target.value)
                        }
                      })
                    }
                    min="1"
                  />
                </div>
                <div>
                  <label className={baseStyles.formLabel} style={{ fontSize: "10px" }}>
                    Metrics
                  </label>
                  <input
                    type="number"
                    className={`${baseStyles.formNumber} ${baseStyles.nodrag}`}
                    value={config.retentionPolicy?.metrics}
                    onChange={(e) =>
                      updateConfig({
                        retentionPolicy: {
                          ...config.retentionPolicy!,
                          metrics: parseInt(e.target.value)
                        }
                      })
                    }
                    min="1"
                  />
                </div>
                <div>
                  <label className={baseStyles.formLabel} style={{ fontSize: "10px" }}>
                    Reports
                  </label>
                  <input
                    type="number"
                    className={`${baseStyles.formNumber} ${baseStyles.nodrag}`}
                    value={config.retentionPolicy?.reports}
                    onChange={(e) =>
                      updateConfig({
                        retentionPolicy: {
                          ...config.retentionPolicy!,
                          reports: parseInt(e.target.value)
                        }
                      })
                    }
                    min="1"
                  />
                </div>
              </div>
            </div> */}

            <button
              className={`${baseStyles.formButton} ${baseStyles.nodrag}`}
              onClick={() => setStatus(status === "active" ? "idle" : "active")}
            >
              {status === "active" ? "Disconnect" : "Connect"} Database
            </button>
          </div>
        </div>
      )}

      <Handle
        type="target"
        position={Position.Left}
        className={baseStyles.targetHandle}
        style={{ background: categoryStyles.handleColor }}
      />
      <Handle
        type="source"
        position={Position.Right}
        className={baseStyles.sourceHandle}
        style={{ background: categoryStyles.handleColor }}
      />

      {/* Show extending connector if source handle is not connected OR node is selected */}
      {shouldShowConnector && (
        <ExtendingConnector
          nodeId={id}
          nodeType="databaseService"
          onNodeSelect={handleNodeSelect}
          compact={isCompactMode}
        />
      )}
    </div>
  );
};

export default memo(DatabaseServiceNode);
